if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
      }

      doesVariantMatchAltText(variant, mediaAlt) {
        if (!variant || !variant.title || variant.title === '') {
          return true;
        }
        
        if (!mediaAlt || mediaAlt.trim() === '') {
          return true;
        }
        
        const braceMatch = mediaAlt.match(/\{([^}]+)\}/);
        
        if (!braceMatch) {
          return true;
        }
        
        const braceContent = braceMatch[1];
        const variantRequirements = braceContent.split(',').map(req => req.trim());
        
        const currentVariantTitle = (variant.title || '').toLowerCase().trim();
        const currentOptions = [
          (variant.option1 || '').toLowerCase().trim(),
          (variant.option2 || '').toLowerCase().trim(),
          (variant.option3 || '').toLowerCase().trim()
        ].filter(opt => opt);
        
        const allVariantText = [currentVariantTitle, ...currentOptions].join(' ').toLowerCase();
        
        for (const requirement of variantRequirements) {
          const requirementParts = requirement.split(/\s*[\/|]\s*/).map(part => part.trim().toLowerCase());
          
          const allPartsMatch = requirementParts.every(part => {
            if (currentVariantTitle === part) return true;
            if (currentOptions.some(opt => opt === part)) return true;
            if (allVariantText.includes(part)) return true;
            return false;
          });
          
          if (allPartsMatch && requirementParts.length > 0) {
            return true;
          }
        }
        
        return false;
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
      
        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );
      
        this.initQuantityHandlers();
      
        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
          
          if (this.isAltTextImageFeatureEnabled) {
            setTimeout(() => {
              this.filterAndRebuildGallery(this.currentVariant);
            }, 500);
          }
        }
      }
      
      get isAltTextImageFeatureEnabled() {
        const infoAttr = this.dataset.altTextImageFeature;
        const mediaGallery = this.querySelector('media-gallery') || document.querySelector('#MediaGallery-' + this.dataset.section);
        const mediaAttr = mediaGallery ? mediaGallery.dataset.altTextImageFeature : null;
        return infoAttr === 'true' || mediaAttr === 'true';
      }
      
      cacheOriginalMedia() {
        const currentThumbContainer = document.querySelector('#Slider-Thumbnails-' + this.dataset.section);
        const currentMediaContainer = document.querySelector('#Slider-Gallery-' + this.dataset.section);
        
        if (currentThumbContainer && currentMediaContainer && (!this.allThumbItems || this.allThumbItems.length === 0)) {
          const thumbs = currentThumbContainer.querySelectorAll('.product__thumbs-item:not(.swiper-slide-duplicate)');
          const media = currentMediaContainer.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)');
          if (thumbs.length > 0 && media.length > 0) {
            this.allThumbItems = Array.from(thumbs).map(item => item.cloneNode(true));
            this.allMediaItems = Array.from(media).map(item => item.cloneNode(true));
          }
        }
      }
      
      filterAndRebuildGallery(variant) {
        if (!this.isAltTextImageFeatureEnabled) return;
        if (!variant || !variant.title) return;
        
        // Skip in quick-view context — quick-view.js handles its own gallery filtering
        // without destroying Swiper (which causes the modal to shrink).
        if (this.dataset.viewMode === 'quick-view' || this.closest('quick-view-modal')) return;
        
        this.cacheOriginalMedia();
        
        const currentThumbContainer = document.querySelector('#Slider-Thumbnails-' + this.dataset.section);
        const currentMediaContainer = document.querySelector('#Slider-Gallery-' + this.dataset.section);
        
        if (!currentThumbContainer || !currentMediaContainer) return;
        
        // Primary: use in-memory cached slides for instant, reliable DOM reconstruction
        if (this.allThumbItems && this.allThumbItems.length > 0 && this.allMediaItems && this.allMediaItems.length > 0) {
          const filteredThumbs = [];
          const filteredMedia = [];
          
          this.allThumbItems.forEach(item => {
            const mediaAlt = item.getAttribute('data-media-alt') || '';
            const mediaType = item.getAttribute('data-media-type') || 'image';
            if (mediaType !== 'image' || this.doesVariantMatchAltText(variant, mediaAlt)) {
              filteredThumbs.push(item.cloneNode(true));
            }
          });
          
          this.allMediaItems.forEach(item => {
            const mediaAlt = item.getAttribute('data-media-alt') || '';
            const mediaType = item.getAttribute('data-media-type') || 'image';
            if (mediaType !== 'image' || this.doesVariantMatchAltText(variant, mediaAlt)) {
              filteredMedia.push(item.cloneNode(true));
            }
          });
          
          const mediaGallery = document.querySelector('#MediaGallery-' + this.dataset.section);
          
          currentThumbContainer.innerHTML = '';
          currentMediaContainer.innerHTML = '';
          
          filteredThumbs.forEach(thumb => currentThumbContainer.appendChild(thumb));
          filteredMedia.forEach(media => currentMediaContainer.appendChild(media));
          
          if (mediaGallery && typeof mediaGallery.reinitSwiper === 'function') {
            mediaGallery.reinitSwiper();
          } else if (mediaGallery && typeof mediaGallery.init === 'function') {
            const viewer = document.querySelector('#GalleryViewer-' + this.dataset.section);
            const thumbnails = document.querySelector('#GalleryThumbnails-' + this.dataset.section);
            if (viewer?.swiper) viewer.swiper.destroy(true, true);
            if (thumbnails?.swiper) thumbnails.swiper.destroy(true, true);
            mediaGallery.init();
          }
          
          setTimeout(() => {
            this.updatePhotoSwipeForVariant(variant);
            this.interceptSwiperNavigation();
            this.setupCustomNavigationListeners();
            this.setupThumbnailClickHandler();
          }, 100);
          
          return;
        }
        
        // Fallback: fetch HTML
        const url = `${this.dataset.url}?variant=${variant.id}&section_id=${this.sectionId}`;
        
        fetch(url)
          .then(response => response.text())
          .then(responseText => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            
            const newThumbnailsContainer = html.querySelector('#Slider-Thumbnails-' + this.sectionId) || html.querySelector('[id^="Slider-Thumbnails"]');
            const newMediaContainer = html.querySelector('#Slider-Gallery-' + this.sectionId) || html.querySelector('[id^="Slider-Gallery"]');
            
            if (!newThumbnailsContainer || !newMediaContainer) return;
            
            const newThumbItems = newThumbnailsContainer.querySelectorAll('.product__thumbs-item');
            const newMediaItems = newMediaContainer.querySelectorAll('.product__media-item');
            
            const filteredThumbs = [];
            const filteredMedia = [];
            
            newThumbItems.forEach(item => {
              const mediaAlt = item.getAttribute('data-media-alt') || '';
              const mediaType = item.getAttribute('data-media-type') || 'image';
              
              if (mediaType !== 'image') {
                filteredThumbs.push(item.cloneNode(true));
              } else {
                if (this.doesVariantMatchAltText(variant, mediaAlt)) {
                  filteredThumbs.push(item.cloneNode(true));
                }
              }
            });
            
            newMediaItems.forEach(item => {
              const mediaAlt = item.getAttribute('data-media-alt') || '';
              const mediaType = item.getAttribute('data-media-type') || 'image';
              
              if (mediaType !== 'image') {
                filteredMedia.push(item.cloneNode(true));
              } else {
                if (this.doesVariantMatchAltText(variant, mediaAlt)) {
                  filteredMedia.push(item.cloneNode(true));
                }
              }
            });
            
            const mediaGallery = document.querySelector('#MediaGallery-' + this.dataset.section);
            
            currentThumbContainer.innerHTML = '';
            currentMediaContainer.innerHTML = '';
            
            filteredThumbs.forEach(thumb => {
              currentThumbContainer.appendChild(thumb);
            });
            
            filteredMedia.forEach(media => {
              currentMediaContainer.appendChild(media);
            });
            
            if (mediaGallery && typeof mediaGallery.reinitSwiper === 'function') {
              mediaGallery.reinitSwiper();
            } else if (mediaGallery && typeof mediaGallery.init === 'function') {
              const viewer = document.querySelector('#GalleryViewer-' + this.dataset.section);
              const thumbnails = document.querySelector('#GalleryThumbnails-' + this.dataset.section);
              if (viewer?.swiper) viewer.swiper.destroy(true, true);
              if (thumbnails?.swiper) thumbnails.swiper.destroy(true, true);
              mediaGallery.init();
            }
            
            setTimeout(() => {
              this.updatePhotoSwipeForVariant(variant);
              this.interceptSwiperNavigation();
              this.setupCustomNavigationListeners();
              this.setupThumbnailClickHandler();
            }, 100);
          })
          .catch(error => {
            console.error('Error rebuilding gallery:', error);
          });
      }
      
      setupThumbnailClickHandler() {
        const thumbContainer = document.querySelector('#Slider-Thumbnails-' + this.dataset.section);
        if (!thumbContainer) return;
        
        // Remove existing listener and add new one
        const clickHandler = (event) => {
          const thumbItem = event.target.closest('.product__thumbs-item');
          if (!thumbItem) return;
          
          if (window.getComputedStyle(thumbItem).display === 'none') return;
          
          const mainSwiper = document.querySelector('#GalleryViewer-' + this.dataset.section)?.swiper;
          if (!mainSwiper) return;
          
          const allThumbs = Array.from(thumbContainer.querySelectorAll('.product__thumbs-item'));
          const thumbIndex = allThumbs.indexOf(thumbItem);
          if (thumbIndex === -1) return;
          
          const mediaId = thumbItem.getAttribute('data-target');
          const mainSlides = mainSwiper.slides;
          
          for (let i = 0; i < mainSlides.length; i++) {
            if (mainSlides[i] && mainSlides[i].getAttribute('data-media-id') == mediaId) {
              mainSwiper.slideTo(i);
              break;
            }
          }
          
          const thumbsSwiper = thumbContainer.closest('.swiper')?.swiper;
          if (thumbsSwiper) {
            thumbsSwiper.slideTo(thumbIndex);
            thumbsSwiper.updateSlidesClasses();
          }
          
          event.preventDefault();
        };
        
        // Remove old listener and add new one
        thumbContainer.removeEventListener('click', this._thumbClickHandler);
        this._thumbClickHandler = clickHandler;
        thumbContainer.addEventListener('click', this._thumbClickHandler);
      }
      
      updateNavigationState() {
        const mainSwiper = document.querySelector('#GalleryViewer-' + this.dataset.section)?.swiper;
        if (!mainSwiper) return;
        
        const nextButton = mainSwiper.el.querySelector('.swiper-button-next');
        const prevButton = mainSwiper.el.querySelector('.swiper-button-prev');
        
        if (nextButton && prevButton) {
          const visibleSlides = Array.from(mainSwiper.slides).filter(slide => {
            return slide && slide.style.display !== 'none';
          });
          
          if (visibleSlides.length <= 1) {
            nextButton.style.display = 'none';
            prevButton.style.display = 'none';
          } else {
            nextButton.style.display = '';
            prevButton.style.display = '';
          }
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      get productForm() {
        return this.querySelector('form[is="product-form"');
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.resetFormState();
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
        };
      }
      
      updateThumbnails(variant, html) {
        if (!variant) return;
        if (this.isAltTextImageFeatureEnabled) {
          this.filterAndRebuildGallery(variant);
        } else {
          this.updateMedia(variant);
        }
      }

      updatePhotoSwipeForVariant(variant) {
        const mediaGallery = document.querySelector('#MediaGallery-' + this.dataset.section);
        if (mediaGallery && typeof mediaGallery.updateImageZoomForVariant === 'function') {
          mediaGallery.updateImageZoomForVariant(variant);
        }
      }
      
      updateMainMediaSlider(variant, html) {
        if (!variant) return;
        if (this.isAltTextImageFeatureEnabled) {
          this.filterAndRebuildGallery(variant);
        } else {
          this.updateMedia(variant);
        }
      }

      interceptSwiperNavigation() {
        const mainSwiper = document.querySelector('#GalleryViewer-' + this.dataset.section)?.swiper;
        if (!mainSwiper) return;
        
        const originalSlideTo = mainSwiper.slideTo.bind(mainSwiper);
        const originalSlideNext = mainSwiper.slideNext.bind(mainSwiper);
        const originalSlidePrev = mainSwiper.slidePrev.bind(mainSwiper);
        
        const getVisibleIndices = () => {
          const visibleIndices = [];
          for (let i = 0; i < mainSwiper.slides.length; i++) {
            if (mainSwiper.slides[i] && mainSwiper.slides[i].style.display !== 'none') {
              visibleIndices.push(i);
            }
          }
          return visibleIndices;
        };
        
        mainSwiper.slideTo = (index, speed = 300, runCallbacks = true, internal) => {
          const visibleIndices = getVisibleIndices();
          if (visibleIndices.length === 0) return;
          
          if (mainSwiper.slides[index] && mainSwiper.slides[index].style.display === 'none') {            
            let closestIndex = visibleIndices[0];
            let minDistance = Math.abs(index - closestIndex);
            for (let i = 1; i < visibleIndices.length; i++) {
              const distance = Math.abs(index - visibleIndices[i]);
              if (distance < minDistance) {
                minDistance = distance;
                closestIndex = visibleIndices[i];
              }
            }
            return originalSlideTo(closestIndex, speed, runCallbacks, internal);
          }
          return originalSlideTo(index, speed, runCallbacks, internal);
        };
        
        mainSwiper.slideNext = (speed, runCallbacks, internal) => {
          const visibleIndices = getVisibleIndices();
          if (visibleIndices.length <= 1) return originalSlideNext(speed, runCallbacks, internal);
          
          const currentIndex = mainSwiper.activeIndex;
          let currentVisibleIndex = visibleIndices.indexOf(currentIndex);
          if (currentVisibleIndex === -1) return originalSlideTo(visibleIndices[0], speed, runCallbacks, internal);
          
          let nextVisibleIndex = currentVisibleIndex + 1;
          if (nextVisibleIndex >= visibleIndices.length) nextVisibleIndex = 0;
          
          const nextIndex = visibleIndices[nextVisibleIndex];
          return mainSwiper.slideTo(nextIndex, speed, runCallbacks, internal);
        };
        
        mainSwiper.slidePrev = (speed, runCallbacks, internal) => {
          const visibleIndices = getVisibleIndices();
          if (visibleIndices.length <= 1) return originalSlidePrev(speed, runCallbacks, internal);
          
          const currentIndex = mainSwiper.activeIndex;
          let currentVisibleIndex = visibleIndices.indexOf(currentIndex);
          if (currentVisibleIndex === -1) return originalSlideTo(visibleIndices[visibleIndices.length - 1], speed, runCallbacks, internal);
          
          let prevVisibleIndex = currentVisibleIndex - 1;
          if (prevVisibleIndex < 0) prevVisibleIndex = visibleIndices.length - 1;
          
          const prevIndex = visibleIndices[prevVisibleIndex];
          return mainSwiper.slideTo(prevIndex, speed, runCallbacks, internal);
        };
        
        mainSwiper._originalSlideTo = originalSlideTo;
        mainSwiper._originalSlideNext = originalSlideNext;
        mainSwiper._originalSlidePrev = originalSlidePrev;
      }
      
      setupCustomNavigationListeners() {
        const mainSwiper = document.querySelector('#GalleryViewer-' + this.dataset.section)?.swiper;
        if (!mainSwiper) return;
        
        const nextButton = mainSwiper.el.querySelector('.swiper-button-next');
        const prevButton = mainSwiper.el.querySelector('.swiper-button-prev');
        
        if (nextButton) {
          const newNext = nextButton.cloneNode(true);
          nextButton.parentNode.replaceChild(newNext, nextButton);
          newNext.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            mainSwiper.slideNext();
          });
        }
        
        if (prevButton) {
          const newPrev = prevButton.cloneNode(true);
          prevButton.parentNode.replaceChild(newPrev, prevButton);
          newPrev.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            mainSwiper.slidePrev();
          });
        }
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.currentVariant = variant;
          this.updateMedia(variant);
          
          if (this.isAltTextImageFeatureEnabled && this.dataset.viewMode !== 'quick-view' && !this.closest('quick-view-modal')) {
            setTimeout(() => {
              this.filterAndRebuildGallery(variant);
            }, 300);

            setTimeout(() => {
              this.ensureFeaturedImageIsShown(variant);
            }, 800);
          }

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              if (id === 'specification-tab') {
                const isEmpty = !destination || destination.innerHTML.trim() === '';
                destination.classList.toggle('hidden', isEmpty);
                destination.parentElement.classList.toggle('hidden', isEmpty);             
              } else {
                destination.classList.toggle('hidden', shouldHide(source));
              }
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Barcode', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('specification-tab');

          // Update product-offers-wrapper offer prices when variant price changes
          if (variant && variant.price != null) {
            const offersWrapper = document.querySelector('details.product-offers-wrapper');
            const discountedPriceEl = offersWrapper?.querySelector('#discounted-price');
            const discountPercent = parseFloat(discountedPriceEl?.getAttribute('data-offer-discount')) || 0;
            if (offersWrapper && discountPercent) {
              const savedAmountInCents = Math.round(variant.price * discountPercent / 100);
              const discountedPriceInCents = variant.price - savedAmountInCents;
              const fmt = (cents) => Math.round(cents / 100).toLocaleString('en-IN');

              const priceStrong = offersWrapper.querySelector('summary .popup-main-content span > strong');
              if (priceStrong) priceStrong.textContent = '₹' + fmt(discountedPriceInCents);

              const saveAmountSpan = offersWrapper.querySelector('summary .save-amount');
              if (saveAmountSpan) saveAmountSpan.textContent = ' Save  ₹' + fmt(savedAmountInCents);

              const discountOffEl = offersWrapper.querySelector('.discount-off');
              if (discountOffEl) discountOffEl.textContent = '₹' + fmt(savedAmountInCents);

              const finalPriceEl = offersWrapper.querySelector('.final-price');
              if (finalPriceEl) finalPriceEl.innerHTML = `Get it for ₹<strong>${fmt(discountedPriceInCents)}</strong>`;

              if (discountedPriceEl) discountedPriceEl.setAttribute('data-product-price', variant.price);
            }
          }


          const productTitle = document.querySelector('.bulk-order-form__product-input-element')?.dataset.productName;
          const variantTitleElement = document.querySelector('#productName');
          if(variantTitleElement){
            variantTitleElement.innerHTML = `${productTitle} <br> ${ variant ? variant.title : ''}`;
          }

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
            FoxTheme.variantStrings.soldOut
          );

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: this.currentVariant,
              },
            })
          );

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];
        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);
        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }
        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return;
      
        this.currentVariant = variant;
        
        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            setTimeout(() => {
              productMedia.setActiveMedia(variant);
            }, 200);
            return true;
          }
          return false;
        };
      
        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }
      
      ensureFeaturedImageIsShown(variant) {
        const mainSwiper = document.querySelector('#GalleryViewer-' + this.dataset.section)?.swiper;
        if (!mainSwiper) return;
        
        setTimeout(() => {
          // Guard: Swiper may have been destroyed or unmounted by the time this fires
          if (!mainSwiper.slides || mainSwiper.destroyed) return;
          for (let i = 0; i < mainSwiper.slides.length; i++) {
            const slide = mainSwiper.slides[i];
            // We use visibility:hidden (not display:none) to hide filtered slides
            if (slide && !slide.classList.contains('swiper-slide-duplicate') && slide.style.visibility !== 'hidden') {
              mainSwiper.slideTo(i);
              break;
            }
          }
        }, 300);
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.innerHTML = FoxTheme.variantStrings.addToCart;
        }
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });
        this.setQuantityBoundries();
      }
    }
  );
}

if (!customElements.get('product-promotion-alert')) {
  customElements.define(
    'product-promotion-alert',
    class ProductPromotionAlert extends HTMLElement {
      constructor() {
        super();
        this.abortController = new AbortController();
        this.buttonEl = this.querySelector('button');
        this.buttonEl.addEventListener('click', this.onClick.bind(this), {
          signal: this.abortController.signal,
        });
      }

      onClick(evt) {
        evt.preventDefault();
        this.closest('.product__block').classList.add('hidden');
      }

      disconnectedCallback() {
        this.abortController.abort();
      }
    }
  );
}